import type { DealerClaimEventResponse } from '../../models/entities/dealer-api.model';
import type { ClaimEventHandleRequest, EventHandle } from '../../models/entities/event-handle.model';
import { resolveProcessingClaimedAt } from '../../utils/processing-claimed-at.util';

/** Raised when Dealer answers a claim with 204 — the event was taken first. */
export class EventNotClaimableError extends Error {
  constructor(readonly eventId: string) {
    super(`Event ${eventId} is no longer available to claim.`);
    this.name = 'EventNotClaimableError';
  }
}

type ClaimContext = ClaimEventHandleRequest & {
  location: string;
  firstAlarmDetected: string;
  priority: number;
  alarmCount: number;
};

/**
 * Dealer has no handle resource: a claim is recorded on the event itself, so the
 * handle id is the event id and the tab/agent fields stay client-side.
 */
export function mapDealerClaimToEventHandle(
  response: DealerClaimEventResponse | null | undefined,
  context: ClaimContext,
): EventHandle {
  const event = response?.event;
  if (!event?.eventId) {
    throw new EventNotClaimableError(context.eventId);
  }

  return {
    handleId: event.eventId,
    eventId: event.eventId,
    tabInstanceId: context.tabInstanceId,
    agentEmail: context.agentEmail,
    agentDisplayName: context.agentDisplayName,
    location: event.siteName?.trim() || context.location,
    firstAlarmDetected:
      event.firstAlarmDateTime ?? event.createdDateTime ?? context.firstAlarmDetected,
    priority: Number(event.priority ?? context.priority) || 0,
    alarmCount: Number(event.alarmCount ?? context.alarmCount) || 0,
    claimedAt: resolveProcessingClaimedAt(event.claimedDateTime),
    siteId: Number(event.siteId) || undefined,
    leaseSeconds: response?.leaseSeconds,
  };
}
